import { client, handleRequest, handleRequestBody } from "./client.ts";
import type { Construction } from "./constructions.ts";

export type StageStatus = "pending" | "in_progress" | "completed" | "cancelled";

export type StageKind = "stage" | "substage" | "service";

export const ROOT_PARENT_ID = "00000000-0000-0000-0000-000000000000";

export interface Stage {
	id: string;
	parent_stage_id?: string;
	name: string;
	description?: string;
	status: StageStatus;
	kind: StageKind;
	position: number;
	depth?: number;
	item_number?: string;
	value_cents?: number;
	created_at: string;
	updated_at: string;
}

export interface StagesLock {
	locked: boolean;
	changed_at?: string;
	changed_by?: {
		id: string;
		name: string;
	};
}

export interface StageTotals {
	valued: boolean;
	total_cents: number;
	unvalued_leaves: number;
}

export interface ListStagesResponse {
	count: number;
	items: Stage[];
	construction: Construction;
	lock: StagesLock;
	totals?: StageTotals;
}

export function listStages(
	constructionId: string,
): Promise<ListStagesResponse> {
	return handleRequestBody(() =>
		client.get<ListStagesResponse>(`/constructions/${constructionId}/stages`),
	);
}

export interface GetStageResponse {
	stage: Stage;
	construction: Construction;
}

export function getStage(
	constructionId: string,
	stageId: string,
): Promise<GetStageResponse> {
	return handleRequestBody(() =>
		client.get<GetStageResponse>(
			`/constructions/${constructionId}/stages/${stageId}`,
		),
	);
}

export interface CreateStageInput {
	name: string;
	description?: string;
	parentStageId?: string;
	position?: number;
	kind?: StageKind;
}

interface StageResponse {
	stage: Stage;
}

export async function createStage(
	constructionId: string,
	input: CreateStageInput,
): Promise<Stage> {
	const { stage } = await handleRequestBody(() =>
		client.post<StageResponse>(`/constructions/${constructionId}/stages`, {
			name: input.name,
			description: input.description,
			parent_stage_id: input.parentStageId,
			position: input.position,
			kind: input.kind,
		}),
	);
	return stage;
}

export interface UpdateStageInput {
	name?: string;
	status?: StageStatus;
	description?: string;
	position?: number;
	kind?: StageKind;
	parentStageId?: string;
}

export async function updateStage(
	constructionId: string,
	stageId: string,
	input: UpdateStageInput,
): Promise<Stage> {
	const { stage } = await handleRequestBody(() =>
		client.patch<StageResponse>(
			`/constructions/${constructionId}/stages/${stageId}`,
			{
				name: input.name,
				status: input.status,
				description: input.description,
				position: input.position,
				kind: input.kind,
				parent_stage_id: input.parentStageId,
			},
		),
	);
	return stage;
}

export async function deleteStage(
	constructionId: string,
	stageId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(`/constructions/${constructionId}/stages/${stageId}`),
	);
}

export interface SetStageValueResponse {
	stage_id: string;
	value_cents: number | null;
}

export function setStageValue(
	constructionId: string,
	stageId: string,
	valueCents: number | null,
): Promise<SetStageValueResponse> {
	return handleRequestBody(() =>
		client.put<SetStageValueResponse>(
			`/constructions/${constructionId}/stages/${stageId}/value`,
			{ value_cents: valueCents },
		),
	);
}

export async function setStagesLock(
	constructionId: string,
	locked: boolean,
): Promise<StagesLock> {
	const { lock } = await handleRequestBody(() =>
		client.put<{ lock: StagesLock }>(
			`/constructions/${constructionId}/stages/lock`,
			{ locked },
		),
	);
	return lock;
}
